import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  MdArrowBack,
  MdPersonOutline,
  MdDelete,
  MdLock,
  MdMenuBook,
  MdClose,
  MdChat,
} from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import { Skeleton, SkeletonLines } from "components/ui/Skeleton";
import EmptyState from "components/ui/EmptyState";
import ConfirmDialog from "components/ui/ConfirmDialog";
import {
  instructorStudentsService,
  type InstructorStudentDetails,
  type InstructorStudentCourseSummary,
} from "services/instructorStudentsService";
import { directChatService } from "services/directChatService";
import StudentAvatar from "components/students/StudentAvatar";

export default function StudentDetailsPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const navigate = useNavigate();
  const params = useParams();
  const studentId = Number(params.studentId);

  const [loading, setLoading] = React.useState(true);
  const [student, setStudent] = React.useState<InstructorStudentDetails | null>(null);

  const [removeTarget, setRemoveTarget] =
    React.useState<InstructorStudentCourseSummary | null>(null);
  const [removeError, setRemoveError] = React.useState<string | null>(null);

  const [noteDraft, setNoteDraft] = React.useState("");
  const [savingNote, setSavingNote] = React.useState(false);

  const [tagDraft, setTagDraft] = React.useState("");
  const [tags, setTags] = React.useState<string[]>([]);
  const [savingTags, setSavingTags] = React.useState(false);
  const [openingChat, setOpeningChat] = React.useState(false);

  const reload = React.useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const data = await instructorStudentsService.get(studentId);
      setStudent(data);
      setNoteDraft(data?.note ?? "");
      setTags(Array.isArray(data?.tags) ? data!.tags : []);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  const handleSaveNote = async () => {
    if (!student) return;
    setSavingNote(true);
    try {
      const res = await instructorStudentsService.saveNote(
        student.id,
        noteDraft.trim() || null
      );
      if (res) {
        setNoteDraft(res.note ?? "");
      }
    } finally {
      setSavingNote(false);
    }
  };

  const commitTags = async (next: string[]) => {
    if (!student) return;
    setSavingTags(true);
    try {
      const res = await instructorStudentsService.saveTags(student.id, next);
      setTags(Array.isArray(res?.tags) ? res!.tags : next);
    } finally {
      setSavingTags(false);
    }
  };

  const handleAddTag = () => {
    const val = tagDraft.trim();
    if (!val) return;
    if (tags.includes(val)) {
      setTagDraft("");
      return;
    }
    const next = [...tags, val];
    setTags(next);
    setTagDraft("");
    void commitTags(next);
  };

  const handleRemoveTag = (tag: string) => {
    const next = tags.filter((t) => t !== tag);
    setTags(next);
    void commitTags(next);
  };

  const handleOpenChat = async () => {
    if (!student || openingChat) return;
    setOpeningChat(true);
    try {
      const bootstrap = await directChatService.openWithStudent(student.id);
      const title = encodeURIComponent(bootstrap.peer?.name || student.name || "");
      navigate(`/instructor/messages/${bootstrap.conversation.id}?title=${title}`);
    } finally {
      setOpeningChat(false);
    }
  };

  if (loading && !student) {
    return (
      <div className="w-full space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <SkeletonLines count={4} />
      </div>
    );
  }

  if (!student) {
    return (
      <div className="w-full space-y-4 p-6">
        <BackLink isArabic={isArabic} />
        <EmptyState
          icon={<MdPersonOutline className="h-6 w-6" />}
          title={isArabic ? "لم يتم العثور على الطالب" : "Student not found"}
        />
      </div>
    );
  }

  const stage = student.profile?.stage?.name ?? student.profile?.grade?.name;
  const canView = !!student.can_view;

  return (
    <div className="w-full space-y-5 p-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("..")}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-900 dark:text-gray-200 dark:hover:bg-white/5"
        >
          <MdArrowBack className={`h-4 w-4 ${isArabic ? "rotate-180" : ""}`} />
          <span>{isArabic ? "رجوع" : "Back"}</span>
        </button>
        <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
          {isArabic ? "تفاصيل الطالب" : "Student details"}
        </h1>
      </div>

      {!canView && (
        <div className="flex items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          <MdLock className="h-5 w-5 shrink-0" />
          <span>
            {isArabic
              ? "هذا الطالب ليس مسجّلاً في أي من كورساتك. يمكنك إضافته إلى أحد كورساتك لرؤية/تحرير البيانات."
              : "This student isn't in any of your courses. Enroll them to view/edit details."}
          </span>
        </div>
      )}

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="flex items-start gap-4">
          <StudentAvatar
            name={student.name}
            image={student.image}
            imageUrl={student.image_url}
            className="h-14 w-14"
            iconClassName="h-7 w-7"
          />
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-lg font-bold text-gray-800 dark:text-white">
              {student.name ?? (isArabic ? "طالب" : "Student")}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {stage || "—"}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canView ? (
              <button
                type="button"
                onClick={() => void handleOpenChat()}
                disabled={openingChat}
                className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
              >
                <MdChat className="h-4 w-4" />
                {openingChat
                  ? isArabic
                    ? "جاري الفتح..."
                    : "Opening..."
                  : isArabic
                    ? "رسالة"
                    : "Message"}
              </button>
            ) : null}
            <span className="text-xs text-gray-400">#{student.id}</span>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-sm font-bold text-gray-800 dark:text-white">
            {isArabic ? "الكورسات المشتركة معك" : "Courses with you"}
          </h3>
        </div>

        {student.courses_with_you.length === 0 ? (
          <EmptyState
            icon={<MdMenuBook className="h-6 w-6" />}
            title={isArabic ? "غير مسجّل في أي من كورساتك" : "Not enrolled in any of your courses"}
            description={
              isArabic
                ? "أضف الطالب إلى أحد كورساتك لبدء الإدارة."
                : "The student will appear here after you approve a course join request."
            }
          />
        ) : (
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {student.courses_with_you.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900"
              >
                <Link
                  to={`/instructor/courses/${c.id}/overview`}
                  className="flex min-w-0 flex-1 items-center gap-2 text-sm font-semibold text-gray-800 hover:text-brand-600 dark:text-white dark:hover:text-brand-300"
                >
                  <MdMenuBook className="h-4 w-4 shrink-0 text-brand-500" />
                  <span className="truncate">{c.title}</span>
                </Link>
                <button
                  type="button"
                  onClick={() => setRemoveTarget(c)}
                  className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300"
                >
                  <MdDelete className="h-4 w-4" />
                  {isArabic ? "إزالة" : "Remove"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <h3 className="mb-2 text-sm font-bold text-gray-800 dark:text-white">
          {isArabic ? "ملاحظات (خاصة بك)" : "Notes (private to you)"}
        </h3>
        <textarea
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          disabled={!canView || savingNote}
          rows={4}
          placeholder={isArabic ? "أضف ملاحظاتك عن هذا الطالب..." : "Write notes about this student..."}
          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 disabled:opacity-60 dark:border-white/10 dark:bg-navy-900 dark:text-white"
        />
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={handleSaveNote}
            disabled={!canView || savingNote}
            className="rounded-lg bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {savingNote ? (isArabic ? "جارٍ الحفظ..." : "Saving...") : isArabic ? "حفظ" : "Save"}
          </button>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <h3 className="mb-2 text-sm font-bold text-gray-800 dark:text-white">
          {isArabic ? "الوسوم (خاصة بك)" : "Tags (private to you)"}
        </h3>

        <div className="flex flex-wrap gap-2">
          {tags.length === 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {isArabic ? "لا توجد وسوم حتى الآن." : "No tags yet."}
            </span>
          )}
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700 dark:bg-brand-500/10 dark:text-brand-300"
            >
              {tag}
              {canView && (
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  disabled={savingTags}
                  className="rounded-full p-0.5 hover:bg-brand-100 dark:hover:bg-brand-500/20"
                  aria-label={isArabic ? "إزالة الوسم" : "Remove tag"}
                >
                  <MdClose className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>

        {canView && (
          <div className="mt-3 flex items-center gap-2">
            <input
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              disabled={savingTags}
              placeholder={isArabic ? "أضف وسمًا واضغط Enter" : "Add a tag, press Enter"}
              className="flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 disabled:opacity-60 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
            <button
              type="button"
              onClick={handleAddTag}
              disabled={savingTags || !tagDraft.trim()}
              className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-60 dark:bg-white/10 dark:text-white"
            >
              {isArabic ? "إضافة" : "Add"}
            </button>
          </div>
        )}
      </section>

      <ConfirmDialog
        open={!!removeTarget}
        onClose={() => {
          setRemoveTarget(null);
          setRemoveError(null);
        }}
        title={isArabic ? "إزالة الطالب من الكورس" : "Remove student from course"}
        description={
          <>
            <p>
              {isArabic
                ? `هل تريد إزالة هذا الطالب من كورس "${removeTarget?.title ?? ""}"؟`
                : `Remove this student from course "${removeTarget?.title ?? ""}"?`}
            </p>
            {removeError ? (
              <p className="mt-2 text-sm font-medium text-red-600 dark:text-red-400">{removeError}</p>
            ) : null}
          </>
        }
        confirmLabel={isArabic ? "إزالة" : "Remove"}
        onConfirm={async () => {
          if (!removeTarget || !student) return;
          setRemoveError(null);
          try {
            const removedCourseId = removeTarget.id;
            await instructorStudentsService.unenroll(student.id, removedCourseId);
            const remaining = (student.courses_with_you ?? []).filter(
              (c) => c.id !== removedCourseId
            );
            if (remaining.length === 0) {
              navigate("/instructor/students");
              return;
            }
            await reload();
          } catch (e: any) {
            const message =
              e?.response?.data?.message ??
              (isArabic ? "تعذر إزالة الطالب من الكورس" : "Could not remove student from course");
            setRemoveError(message);
            throw e;
          }
        }}
      />
    </div>
  );
}

function BackLink({ isArabic }: { isArabic: boolean }) {
  return (
    <Link
      to=".."
      className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-900 dark:text-gray-200 dark:hover:bg-white/5"
    >
      <MdArrowBack className={`h-4 w-4 ${isArabic ? "rotate-180" : ""}`} />
      <span>{isArabic ? "رجوع" : "Back"}</span>
    </Link>
  );
}
